import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'

import { datelib, log } from '@nx-superprep/utils'

import exportFromJSON from 'export-from-json'

import { DayPlanModule, isRegularStudyPhase, SpacedRepModuleConfig, StudyPhase, StudySetting } from '../models/back-end'
import { CardService } from './card.service'
import { UserService } from './user.service'
import { Deck, FrontendCard as Card } from '../models/front-end'

export interface DeckUpdateConfig {
  lastAnsweredCardId?: string,
  acquiredIdsToday?: string[],
  cards: Card[],
  setting: StudySetting,
  dayPlanModule: DayPlanModule,
  spacedRepConfig: SpacedRepModuleConfig,
  studyPhase?: StudyPhase,
  reviewIds?: string[],
  isUpdate?: boolean,
  indexForCurrentCardForPractice?: number,
  indexForCurrentCardForReview?: number,
  indexForCurrentCardForQuiz?: number,
}

@Injectable({providedIn: 'root'})
export class DeckService {

  lastReviewOrder: { id: string|undefined, cardIds: string[]} = { id: undefined, cardIds: [] }

  _deck: Deck|undefined = undefined
  public get deck() {
    return this._deck
  }
  public set deck(value) {
    this._deck = value
    if (this._deck) { this._deck.previousSelectedCard = this.previousSelectedCard as unknown as Card; }
    this.selectedDeck.next(value)
  }
  selectedDeck: BehaviorSubject<Deck|undefined> = new BehaviorSubject(this._deck)

  public get card() {
    return this.deck?.getCurrentCard()
  }
  get selectedCard() {
    return this.cardService.selectedItem
  }
  get previousSelectedCard() {
    return this.cardService.previousSelectedCard
  }

  _phase: StudyPhase = StudyPhase.Practice
  public get phase() {
    return this._phase
  }
  public set phase(value) {
    this._phase = value
    if (this.deck && this.deck.phase !== value) {
      this.deck = this.updateData({
        acquiredIdsToday: this.deck.acquiredIdsToday,
        cards: this.deck.cards,
        setting: this.deck.setting,
        dayPlanModule: this.deck.dayPlanModule,
        spacedRepConfig: this.deck.spacedRepConfig,
        studyPhase: value,
        reviewIds: this.deck.dayPlanModule.reviewCardIds ?? this._reviewIds.get(this.deck.dayPlanModule.moduleId) ?? [],
        lastAnsweredCardId: this.deck.lastAnsweredCardId
      })
    }
    this.selectedPhase.next(value)
  }
  selectedPhase: BehaviorSubject<StudyPhase> = new BehaviorSubject(this._phase)

  _repositionedCard: Card|undefined = undefined
  public get repositionedCard() {
    return this._repositionedCard
  }
  public set repositionedCard(value) {
    this._repositionedCard = value
  }
  selectedRepositionedCard: BehaviorSubject<Card|undefined> = new BehaviorSubject(
    this._repositionedCard,
  )

  protected readonly onDestroy = new Subject<void>()
  private readonly _reviewIds = new Map<string, string[]>()

  constructor(
    protected cardService: CardService,
    protected userService: UserService,
  ) {
    userService.userData.subscribe(() => this.clear())
  }

  get currentCards() { return this.deck?.currentCards(this.phase) ?? []; }
  get acquisitionCardIds() { return (this.currentCards.flatMap(c => c.inAcquisition ? c.id ?? [] : [])); }

  getTargetDeckLength(phase: StudyPhase, module: DayPlanModule) {
    switch (phase) {
      case StudyPhase.Review: return module.reviewTarget
      case StudyPhase.Acquisition: return module.acquisitionTarget
      case StudyPhase.Practice:
      case StudyPhase.Quiz:
      default: return 10
    }
  }

  clear(moduleId?: string) {
    this.onDestroy.next()
    if (this.deck) {
      log.info('Deck clearing cache')
      if (this.deck.dayPlanModule.moduleId === moduleId) { this.clearStudylog() }
    }
    this.deck = undefined
    this.phase = StudyPhase.Review
    this.selectedCard.next(undefined)
    this.repositionedCard = undefined
  }

  updateData(options: DeckUpdateConfig) {
    // updating from an alredy existing deck
    options.isUpdate = this.deck?.dayPlanModule.moduleId === options.dayPlanModule.moduleId;

    // When transitioning phases, we should clear the last answered card
    if (options.isUpdate && this.deck?.phase !== options.studyPhase) {
      options.lastAnsweredCardId = undefined;
    }

    if (options.isUpdate) {
      const newIds = options.cards.map(c => c.id);
      const oldCards = (this.deck?.currentCards()??[]).filter(c => !newIds.includes(c.id));
      if (oldCards.length > 0) {
        options.cards = [...oldCards, ...options.cards];
        options.acquiredIdsToday = this.deck?.acquiredIdsToday ?? [];
      }
      options.indexForCurrentCardForPractice = this.deck?.indexForCurrentCardForPractice || 0;
      options.indexForCurrentCardForReview = this.deck?.indexForCurrentCardForReview || 0;
      options.indexForCurrentCardForQuiz = this.deck?.indexForCurrentCardForQuiz || 0;
      // Preserve the last answered card ID only if not transitioning phases
      if (this.deck?.phase === options.studyPhase) {
        options.lastAnsweredCardId = this.deck?.lastAnsweredCardId;
      }
    } else {
      this.clearStudylog();
      this._reviewIds.clear();
    }
    // preserve review deck order on rentry
    options.reviewIds = options.reviewIds ?? this.deck?.dayPlanModule.reviewCardIds ?? this._reviewIds.get(options.dayPlanModule.moduleId) ?? [];
    options.studyPhase = options.studyPhase ?? this.phase;
    const deck = new Deck(options);
    if (deck.isEqual(this.deck)) {
      log.debug(`same deck`, deck.cards);
    } else {
      log.debug(`${deck.phase} deck ${(this.deck?.cards ?? []).length > 0 ? 'update' : 'create'} [${deck.targetLength}] ${deck.dayPlanModule.moduleId}`, deck.cards);
      this.deck = deck;
    }
    this._reviewIds.set(options.dayPlanModule.moduleId, deck.reviewCards.map(c => c.id));
    return this.deck;
  }

  state(deck?: Deck) {
    return !deck ? 'empty' : Deck.isReady(deck) ? 'ready' : 'loading'
  }

  finishStudy(deck = this.deck) {
    log.debug(deck?.name, 'finished study', deck)
    return deck
  }

  processAnswer(
    isCorrect: boolean,
    responseTime: number,
    card = this.card,
    deck = this.deck,
  ): { card?: Card, responseTime?: number, index: number, shouldTransition?: boolean, shouldTerminate?: boolean } {
    if (!card || !deck) { return { index: -1 }}

    // Set the last answered card
    deck.lastAnsweredCardId = card.id;
    // Check if we should transition before processing the answer
    const shouldTransition = deck.phase === StudyPhase.Review && !deck.dayPlanModule.isAcquisitionFinished && deck.isEndOfStudyForReview;
    const shouldTerminate = isRegularStudyPhase(deck.phase) ? deck.isFinishedForStudy : deck.progress >= 1;

    if (!shouldTransition) { deck.increaseIndex() }

    // reposition card still in acquisition into the deck, discard card in review, practice or quiz
    const index = deck.repositionCard(card, isCorrect)
    this.repositionedCard = card
    deck.update()
    this._studylog.push({id:card.id, stats:card.stats})
    return {card, responseTime, index, shouldTransition, shouldTerminate}
  }

  private _studylog: unknown[] = []
  clearStudylog() {
    this._studylog = []
  }
  getStudyLog() {
    return exportFromJSON({
      data: this._studylog,
      fileName: `${datelib().format('YYYYMMDD')}-studylog`,
      fields: {},
      exportType: 'csv',
    })
  }
}
